import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWebSocketData } from "../context/WebSocketContext";
import { runDeployment } from "../redux/deploymentSlice";
import {
  MdTerminal,
  MdRocketLaunch,
  MdShowChart,
  MdCloudOff,
  MdLanguage,
  MdOpenInNew,
  MdStar,
  MdVisibility,
  MdHistory,
  MdExpandMore,
  MdExpandLess
} from "react-icons/md";
import { FaGithub, FaCodeBranch } from "react-icons/fa";
import SystemLogs from "./SystemLogs";
import SearchableSelect from "./ui/SearchableSelect";
import ConfirmModal from "./ui/ConfirmModal";
import { formatDate } from "../utils/units";
import "./ui/SearchableSelect.css";
import "./Monitor.css";

function Monitor() {
  // WebSocket data - continuously streaming
  const topics = useWebSocketData();
  const {
    deployments,
    "script-logs": scriptLogs,
    "pm2-logs": pm2Logs = [],
    "nginx-access-logs": nginxAccessLogs = [],
    "nginx-error-logs": nginxErrorLogs = []
  } = topics;

  // Redux
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.deployment);

  // State
  const [logFilter, setLogFilter] = useState("all"); // all, error, success, info
  const [repoFilter, setRepoFilter] = useState("all");
  const [expandedDeployments, setExpandedDeployments] = useState({});
  const [activeSystemLogTab, setActiveSystemLogTab] = useState('pm2'); // pm2, nginx-access, nginx-error
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);
  const logsEndRef = useRef(null);

  // Auto-scroll logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scriptLogs]);

  // Run Deployment
  const handleDeployClick = () => {
    setShowDeployConfirm(true);
  };

  const confirmDeployment = async () => {
    setShowDeployConfirm(false);
    try {
      await dispatch(runDeployment()).unwrap();
    } catch (err) {
      console.error("Deployment error:", err);
    }
  };

  // Get log color based on type
  const getLogColor = (type) => {
    switch (type) {
      case "error":
      case "stderr":
        return "text-danger";
      case "success":
        return "text-success";
      case "start":
      case "info":
        return "text-primary";
      default:
        return "text-light";
    }
  };

  // Get log icon
  const getLogIcon = (type) => {
    switch (type) {
      case "error":
      case "stderr":
        return "❌";
      case "success":
        return "✅";
      case "start":
        return "🚀";
      case "info":
        return "ℹ️";
      default:
        return "📝";
    }
  };

  // Filter logs
  const filteredLogs = scriptLogs?.filter(log => {
    if (logFilter === "all") return true;
    if (logFilter === "error") return log.type === "error" || log.type === "stderr";
    if (logFilter === "success") return log.type === "success";
    if (logFilter === "info") return log.type === "info" || log.type === "start";
    return true;
  }) || [];

  // Group deployments by deploymentId
  const groupedDeployments = useMemo(() => {
    const groups = {};
    deployments.forEach(dep => {
      const id = dep.deploymentId || dep.timestamp;
      if (!groups[id]) {
        groups[id] = {
          id,
          domain: dep.domain || dep.projectDomain,
          repoName: dep.repoName,
          githubRepo: dep.githubRepo,
          startTime: dep.timestamp,
          status: dep.type === 'error' ? 'failed' : dep.phase === 'complete' ? 'success' : 'running',
          logs: [],
          commit: dep.commit || null,
          phase: dep.phase
        };
      }
      groups[id].logs.push(dep);
      // Update status based on latest log
      if (dep.type === 'error') groups[id].status = 'failed';
      else if (dep.phase === 'complete') groups[id].status = 'success';
    });
    return Object.values(groups).sort((a, b) =>
      new Date(b.startTime) - new Date(a.startTime)
    );
  }, [deployments]);

  // Get unique repos for filter
  const uniqueRepos = useMemo(() => {
    const repos = new Set();
    groupedDeployments.forEach(dep => {
      if (dep.repoName) repos.add(dep.repoName);
    });
    return Array.from(repos).sort();
  }, [groupedDeployments]);

  // Filter deployments by repo
  const filteredDeployments = useMemo(() => {
    if (repoFilter === 'all') return groupedDeployments;
    return groupedDeployments.filter(dep => dep.repoName === repoFilter);
  }, [groupedDeployments, repoFilter]);

  // Toggle deployment expansion
  const toggleDeployment = (id) => {
    setExpandedDeployments(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="monitor-container">
      <div className="monitor-header">
        <div>
          <h3 className="monitor-title">
            Live Deployment Monitor
          </h3>
          <p className="monitor-subtitle">
            Real-time deployment logs & GitHub status
          </p>
        </div>
        <button onClick={handleDeployClick} className="btn btn-dark" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Deploying...
            </>
          ) : (
            <>
              <MdRocketLaunch className="me-2" size={18} /> Pull & Deploy Dashboard
            </>
          )}
        </button>
      </div>

      {/* Main Grid Layout */}
      <div className="monitor-grid">
        {/* Left: Live Deployment Logs */}
        <div className="monitor-card logs-card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                Deployment Logs
              </h6>
              <span className="badge bg-primary rounded-pill">{scriptLogs?.length || 0} logs</span>
            </div>

            {/* Log Filters */}
            <div className="log-filters">
              <button
                className={`filter-btn ${logFilter === 'all' ? 'active' : ''}`}
                onClick={() => setLogFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${logFilter === 'info' ? 'active' : ''}`}
                onClick={() => setLogFilter('info')}
              >
                Info
              </button>
              <button
                className={`filter-btn ${logFilter === 'success' ? 'active' : ''}`}
                onClick={() => setLogFilter('success')}
              >
                Success
              </button>
              <button
                className={`filter-btn ${logFilter === 'error' ? 'active' : ''}`}
                onClick={() => setLogFilter('error')}
              >
                Errors
              </button>
            </div>
          </div>

          <div className="terminal-view">
            {filteredLogs.length === 0 ? (
              <div className="no-logs">
                <div className="mb-3">
                  <MdTerminal className="text-secondary" style={{ fontSize: '2rem' }} />
                </div>
                <p>No deployment logs yet...</p>
                <small>Logs will appear here automatically when deployments run</small>
              </div>
            ) : (
              filteredLogs.slice().reverse().map((log, index) => (
                <div key={index} className="log-line">
                  <span className="log-icon">{getLogIcon(log.type)}</span>
                  <span className="log-time">
                    {formatDate(log.timestamp, "time-only")}
                  </span>
                  <span className={`log-type ${getLogColor(log.type)}`}>
                    [{log.type.toUpperCase()}]
                  </span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>

        {/* Right: Live GitHub & Deployment Status */}
        <div className="monitor-card status-card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <MdShowChart className="me-2" size={18} /> Deployment Status
              </h6>
              <span className="badge bg-success rounded-pill">{filteredDeployments?.length || 0} deployments</span>
            </div>

            {/* Repository Filter */}
            {uniqueRepos.length > 0 && (
              <SearchableSelect
                value={repoFilter}
                onChange={(val) => setRepoFilter(val)}
                options={[
                  { value: "all", label: `All Repositories (${groupedDeployments.length})` },
                  ...uniqueRepos.map(repo => ({ value: repo, label: repo }))
                ]}
                placeholder="All Repositories"
                searchPlaceholder="Search repos..."
              />
            )}
          </div>

          <div className="status-list">
            {!filteredDeployments || filteredDeployments.length === 0 ? (
              <div className="no-deployments">
                <div className="mb-3">
                  <MdCloudOff className="text-secondary" style={{ fontSize: '2rem' }} />
                </div>
                <p>No deployments yet...</p>
                <small>Deployment status will appear here</small>
              </div>
            ) : (filteredDeployments.map((deployment) => (
              <div key={deployment.id} className="deployment-item">
                <div className="deployment-header mb-0">
                  <div className="deployment-info">
                    <div className="deployment-domain">
                      <MdLanguage className="me-2 text-primary" size={16} />
                      {deployment.domain}
                    </div>
                    <div className="deployment-repo text-muted small">
                      <FaGithub className="me-1" size={12} />
                      {deployment.repoName}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`status-badge ${deployment.status}`}>
                      {deployment.status}
                    </span>
                  </div>
                </div>

                {/* GitHub Repo Link */}
                {deployment.githubRepo && (
                  <div className="repo-link-container mt-2 mb-3 pt-0 border-0">
                    <a
                      href={deployment.githubRepo}
                      target="_blank"
                      rel="noreferrer"
                      className="repo-link"
                    >
                      <FaGithub className="me-1" size={14} />
                      View Repository <MdOpenInNew className="ms-1" size={12} />
                    </a>
                  </div>
                )}

                {/* Timeline View */}
                <div className="deployment-timeline">
                  {/* 1. Webhook / Start */}
                  <div className="timeline-item info">
                    <div className="timeline-icon">🚀</div>
                    <div className="timeline-content">
                      <span className="timeline-time">{formatDate(deployment.startTime, "time-only")}</span>
                      <div className="timeline-message">Deployment triggered</div>
                      {deployment.commit && (
                        <div className="timeline-meta">
                          <div><strong>Commit:</strong> {deployment.commit.message}</div>
                          <div className="mt-1"><small className="text-muted">{deployment.commit.author} • <code className="commit-hash">{deployment.commit.hash.substring(0, 7)}</code></small></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 2. Key Logs (Filtered) */}
                  {deployment.logs.map((log, i) => {
                    // Only show significant logs in timeline to avoid clutter
                    const isSignificant =
                      log.type === 'error' ||
                      log.type === 'success' ||
                      (log.message && (
                        log.message.includes('Updating') ||
                        log.message.includes('changed') ||
                        log.message.includes('installing') ||
                        log.message.includes('build')
                      ));

                    if (!isSignificant) return null;

                    return (
                      <div key={i} className={`timeline-item ${log.type === 'error' ? 'error' : log.type === 'success' ? 'success' : 'info'}`}>
                        <div className="timeline-icon">{getLogIcon(log.type)}</div>
                        <div className="timeline-content">
                          <span className="timeline-time">{formatDate(log.timestamp, "time-only")}</span>
                          <div className="timeline-message">{log.message}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
            )}
          </div>
        </div>
      </div>

      {/* System Logs Section */}
      <div className="monitor-card mt-4">
        <div className="card-header">
          <h6 className="mb-3">
            <MdTerminal className="me-2" size={18} />
            System Logs
          </h6>

          {/* Tabs */}
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeSystemLogTab === 'pm2' ? 'active' : ''}`}
                onClick={() => setActiveSystemLogTab('pm2')}
                type="button"
              >
                PM2 Logs
                <span className="badge bg-secondary ms-2">{pm2Logs.length}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeSystemLogTab === 'nginx-access' ? 'active' : ''}`}
                onClick={() => setActiveSystemLogTab('nginx-access')}
                type="button"
              >
                Nginx Access
                <span className="badge bg-secondary ms-2">{nginxAccessLogs.length}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeSystemLogTab === 'nginx-error' ? 'active' : ''}`}
                onClick={() => setActiveSystemLogTab('nginx-error')}
                type="button"
              >
                Nginx Errors
                <span className="badge bg-secondary ms-2">{nginxErrorLogs.length}</span>
              </button>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeSystemLogTab === 'pm2' && (
            <SystemLogs
              logs={pm2Logs}
              type="pm2"
              title="PM2 Process Logs"
            />
          )}
          {activeSystemLogTab === 'nginx-access' && (
            <SystemLogs
              logs={nginxAccessLogs}
              type="nginx-access"
              title="Nginx Access Logs"
            />
          )}
          {activeSystemLogTab === 'nginx-error' && (
            <SystemLogs
              logs={nginxErrorLogs}
              type="nginx-error"
              title="Nginx Error Logs"
            />
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        show={showDeployConfirm}
        onCancel={() => setShowDeployConfirm(false)}
        onConfirm={confirmDeployment}
        title="Pull & Deploy Dashboard"
        message="It will deploy the MC Server both frontend and backend, with kafka, zookeeper, by pulling the latest changes from the github."
        confirmText="Confirm & Deploy"
        confirmVariant="danger"
        confirmPhrase="confirm"
      />
    </div>
  );
}

export default Monitor;
